import React, { useState, useEffect } from 'react';
import { Clock, Users, Vote, CheckCircle, Timer, Settings, Mic, Award } from 'lucide-react';
import QuorumStatus from '../common/QuorumStatus';
import JoinQrCard from '../meeting/JoinQrCard';
import { getQuorumStatus } from '../../utils/quorumUtils';

const ElectionObserverView = ({
  theme = 'light',
  meetingSetup = {},
  currentElection = null,
  candidateSpeechQueue = [],
  currentCandidate = null,
  speakerTimer = 0,
  isTimerRunning = false,
  meetingTimer = 0,
  // New props to match the redesigned elections module
  electionScreen = 1, // 1: setup, 2: nominations, 3: speeches, 4: voting, 5: results
  nominees = [],
  ballotVotes = {},
  writeInCandidates = [],
  invalidBallots = 0,
  voteJoinCode = null,
  enableQrDeviceVoting = false,
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const cardClass = theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white text-gray-900';
  const accentClass = theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50';

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === null || seconds === undefined) {
      seconds = 0;
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Breadcrumb stages for observer view (excluding setup)
  const breadcrumbs = [
    { id: 2, label: 'Nominations', icon: Users, active: electionScreen === 2 },
    { id: 3, label: 'Speeches', icon: Mic, active: electionScreen === 3 },
    { id: 4, label: 'Voting', icon: Vote, active: electionScreen === 4 },
    { id: 5, label: 'Results', icon: Award, active: electionScreen === 5 }
  ];

  const getElectionStatusText = () => {
    if (!currentElection) return 'No Election Active';

    switch (electionScreen) {
      case 1:
        return 'Setting Up Election';
      case 2:
        const acceptedCount = nominees.filter(n => n.status === 'accepted').length;
        return `Nominations Open - ${acceptedCount} Accepted`;
      case 3:
        const spokenCount = nominees.filter(n => n.status === 'accepted' && n.hasSpoken).length;
        const totalSpeakers = nominees.filter(n => n.status === 'accepted').length;
        return `Speeches: ${spokenCount} of ${totalSpeakers} Complete`;
      case 4:
        return 'Voting in Progress';
      case 5:
        return 'Election Complete';
      default:
        return 'Election in Progress';
    }
  };

  const getElectionWinners = () => {
    if (electionScreen !== 5) return [];

    const allCandidates = [
      ...nominees.filter(n => n.status === 'accepted').map(n => ({ name: n.name, votes: ballotVotes[n.id] || 0 })),
      ...writeInCandidates
    ];

    if (allCandidates.length === 0) return [];

    // Sort by votes descending and take the top seatsToFill candidates
    const sortedCandidates = allCandidates.sort((a, b) => b.votes - a.votes);
    const seatsToFill = currentElection?.seatsToFill || 1;

    return sortedCandidates.slice(0, seatsToFill);
  };

  // Legacy single winner function for backwards compatibility
  const getElectionWinner = () => {
    const winners = getElectionWinners();
    return winners.length > 0 ? winners[0] : null;
  };

  const getTotalVotes = () => {
    if (electionScreen !== 5) return 0;

    const nomineeVotes = Object.values(ballotVotes).reduce((sum, votes) => sum + votes, 0);
    const writeInVotes = writeInCandidates.reduce((sum, candidate) => sum + candidate.votes, 0);
    return nomineeVotes + writeInVotes + invalidBallots;
  };

  // If no election is active, show waiting state
  if (!currentElection) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'} p-6`}>
        <div className="max-w-4xl mx-auto">
          <div className={`${cardClass} rounded-lg shadow-lg p-12 text-center`}>
            <Vote size={64} className="text-gray-400 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-600 mb-4">Election Observer View</h1>
            <p className="text-gray-500 text-lg">
              Waiting for election to begin...
            </p>
            {meetingSetup.organizationName && (
              <p className="text-gray-400 mt-2">{meetingSetup.organizationName}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'} p-4`}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className={`${cardClass} rounded-lg shadow-lg p-6 mb-6`}>
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-4xl font-bold mb-2">
                {meetingSetup.organizationName || 'Parliamentary Meeting'}
              </h1>
              <div className="flex items-center gap-6 text-lg">
                <span className="flex items-center gap-2">
                  <Clock size={20} />
                  {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="flex items-center gap-2">
                  <Users size={20} />
                  {meetingSetup.presentMembers || 0} / {meetingSetup.totalMembers || 0} Present
                </span>
                <span className="flex items-center gap-2">
                  <Timer size={20} />
                  Meeting:{' '}
                  <span className="inline-block min-w-[5ch] font-mono tabular-nums">
                    {formatTime(meetingTimer)}
                  </span>
                </span>
                {meetingSetup.totalMembers > 0 && (
                  <QuorumStatus
                    quorumStatus={getQuorumStatus(meetingSetup)}
                    size="normal"
                    className="shrink-0"
                  />
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-start gap-3">
              {enableQrDeviceVoting ? (
                voteJoinCode ? (
                  <JoinQrCard joinCode={voteJoinCode} />
                ) : (
                  <div className="max-w-[11rem] rounded-xl border border-dashed border-slate-300 bg-white/90 p-3 text-center text-xs text-slate-500">
                    Voting QR appears when the chair’s meeting room is ready.
                  </div>
                )
              ) : null}
              <div className="text-right">
                <div className="flex items-center gap-2 text-orange-600 mb-1">
                  <Vote size={24} />
                  <span className="text-xl font-bold">Election</span>
                </div>
                {currentElection && (
                  <p className="text-lg font-medium">{currentElection.office}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Election Progress Breadcrumbs */}
        <div className={`${cardClass} rounded-lg shadow-lg p-3 mb-4`}>
          <h2 className="text-lg font-bold mb-3">Election Progress</h2>
          <div className="flex items-center justify-between">
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full text-xs font-medium border-2 ${
                  electionScreen === crumb.id ? 'bg-orange-500 border-orange-500 text-white' :
                  electionScreen > crumb.id ? 'bg-green-500 border-green-500 text-white' :
                  'bg-gray-300 border-gray-300 text-gray-600'
                }`}>
                  {electionScreen > crumb.id ? <CheckCircle size={16} /> : <crumb.icon size={16} />}
                </div>
                <span className={`ml-2 text-xs font-medium ${
                  electionScreen === crumb.id ? 'text-orange-600' :
                  electionScreen > crumb.id ? 'text-green-600' :
                  'text-gray-500'
                }`}>
                  {crumb.label}
                </span>
                {index < breadcrumbs.length - 1 && (
                  <div className={`w-12 h-1 mx-3 ${
                    electionScreen > crumb.id ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Left Column - Election Details */}
          <div className={`${cardClass} rounded-lg shadow-lg p-4`}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Vote size={18} />
              Election Details
            </h2>

            {/* Current Status */}
            <div className={`p-3 rounded-lg border-2 mb-3 ${
              electionScreen === 5 ? 'border-green-300 bg-green-50' :
              electionScreen === 4 ? 'border-blue-300 bg-blue-50' :
              electionScreen >= 2 ? 'border-orange-300 bg-orange-50' : 'border-gray-300 bg-gray-50'
            }`}>
              <h3 className="font-semibold mb-1 text-base">Status</h3>
              <p className="text-sm">{getElectionStatusText()}</p>
            </div>

            {/* Election Settings (Screen 1-2) */}
            {electionScreen <= 2 && (
              <div className={`p-2 rounded mb-2 ${accentClass}`}>
                <h3 className="font-semibold mb-1 text-gray-700 text-base">Election Settings</h3>
                <div className="text-sm space-y-1">
                  <div>Office: {currentElection.office}</div>
                  {currentElection.speechTimeLimit && (
                    <div>Speech Limit: {currentElection.speechTimeLimit} min</div>
                  )}
                  {currentElection.secretBallot !== undefined && (
                    <div>Ballot Type: {currentElection.secretBallot ? 'Secret' : 'Open'}</div>
                  )}
                </div>
              </div>
            )}

            {/* Nominees List (Screen 2+) */}
            {electionScreen >= 2 && nominees.length > 0 && (
              <div className="mb-2">
                <h3 className="font-semibold mb-1 text-gray-700 text-sm">
                  Nominees ({nominees.filter(n => n.status === 'accepted').length} accepted)
                </h3>
                <div className={`grid gap-1 ${nominees.filter(n => n.status === 'accepted').length > 6 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {nominees.filter(n => n.status === 'accepted').map((nominee) => (
                    <div key={nominee.id} className={`p-2 rounded flex items-center justify-between ${accentClass}`}>
                      <div className="flex items-center gap-2">
                        <CheckCircle size={12} className="text-green-600" />
                        <span className="font-medium text-sm">{nominee.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {nominee.hasSpoken && electionScreen >= 3 && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-medium">
                            Spoken
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Election Statistics */}
            <div className={`p-3 rounded ${accentClass}`}>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Total Nominees:</span>
                  <span className="font-medium">{nominees.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Accepted:</span>
                  <span className="font-medium">{nominees.filter(n => n.status === 'accepted').length}</span>
                </div>
                {electionScreen >= 3 && (
                  <div className="flex justify-between">
                    <span>Speeches Complete:</span>
                    <span className="font-medium">
                      {nominees.filter(n => n.status === 'accepted' && n.hasSpoken).length}
                    </span>
                  </div>
                )}
                {electionScreen === 5 && (
                  <div className="flex justify-between">
                    <span>Total Votes:</span>
                    <span className="font-medium">{getTotalVotes()}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center Column - Speech Timer & Queue */}
          <div className={`${cardClass} rounded-lg shadow-lg p-4`}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Timer size={18} />
              Speech Timer
            </h2>

            {/* Current Speaker Display */}
            <div className="text-center mb-4">
              <div className={`text-4xl font-mono font-bold mb-2 ${
                (() => {
                  if (!currentElection?.speechTimeLimit) return 'text-gray-600';
                  const timeLimit = currentElection.speechTimeLimit * 60; // Convert to seconds
                  const percentageLeft = speakerTimer / timeLimit;
                  if (percentageLeft > 0.5) return 'text-green-600';
                  if (percentageLeft > 0.25) return 'text-yellow-600';
                  return 'text-red-600';
                })()
              }`}>
                {formatTime(speakerTimer)}
              </div>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                isTimerRunning && currentCandidate ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {currentCandidate ? `${currentCandidate.name} Speaking` :
                 electionScreen >= 3 ? 'No Current Speaker' : 'Speeches Not Started'}
              </div>
            </div>

            {/* Speech Queue */}
            {electionScreen >= 3 && candidateSpeechQueue && candidateSpeechQueue.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 text-gray-700 text-base">Upcoming Speakers</h3>
                <div className="space-y-2">
                  {candidateSpeechQueue
                    .filter(candidate => !currentCandidate || candidate.id !== currentCandidate.id)
                    .slice(0, 4)
                    .map((candidate, index) => (
                    <div key={candidate.id} className={`p-3 rounded text-sm ${accentClass}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{candidate.name}</span>
                        {index === 0 && (
                          <span className="text-xs text-orange-600 font-medium">Next</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pre-speech info */}
            {electionScreen < 3 && (
              <div className="text-center py-2">
                <div className={`p-4 rounded-lg ${accentClass}`}>
                  <p className="text-sm text-gray-600">
                    {electionScreen === 1 ? 'Election setup in progress...' :
                     electionScreen === 2 ? 'Waiting for nominations to close...' :
                     'Preparing for speeches...'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Election Results */}
          <div className={`${cardClass} rounded-lg shadow-lg p-4`}>
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
              <Award size={18} />
              Results
            </h2>

            {/* Winner Display */}
            {electionScreen === 5 ? (
              (() => {
                const winners = getElectionWinners();
                const seatsToFill = currentElection?.seatsToFill || 1;

                return winners.length > 0 ? (
                  <div className="text-center mb-6">
                    <div className="p-4 rounded-lg bg-green-50 border-2 border-green-200 mb-4">
                      <Award size={32} className="text-green-500 mx-auto mb-2" />
                      <h3 className="text-lg font-bold text-green-800 mb-2">
                        {seatsToFill === 1 ? 'Winner' : 'Winners'}
                      </h3>
                      {seatsToFill === 1 ? (
                        <p className="text-xl font-bold text-green-900">
                          {winners[0].name}
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {winners.map((winner, index) => (
                            <p key={index} className="text-lg font-bold text-green-900">
                              {winner.name}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center mb-6">
                    <div className="p-4 rounded-lg bg-yellow-50 border-2 border-yellow-200 mb-4">
                      <Award size={32} className="text-yellow-500 mx-auto mb-2" />
                      <h3 className="text-lg font-bold text-yellow-800 mb-1">Election Complete</h3>
                      <p className="text-lg text-yellow-700">
                        No candidates received votes
                      </p>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="text-center mb-6">
                <div className={`p-4 rounded-lg ${accentClass} border-2 border-gray-200`}>
                  <Vote size={32} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {electionScreen < 5 ? 'Election in progress...' : 'Awaiting results...'}
                  </p>
                </div>
              </div>
            )}

            {/* Vote Breakdown - Only if setting is enabled and election is complete */}
            {electionScreen === 5 && currentElection?.showVoteBreakdown && (
              <div className="mb-4">
                <h3 className="text-base font-bold mb-3 text-gray-700">Vote Breakdown</h3>
                <div className="space-y-2">
                  {/* Nominees */}
                  {nominees.filter(n => n.status === 'accepted').map((nominee) => (
                    <div key={nominee.id} className={`p-2 rounded flex items-center justify-between ${accentClass}`}>
                      <span className="font-medium text-sm">{nominee.name}</span>
                      <span className="font-bold text-sm">{ballotVotes[nominee.id] || 0}</span>
                    </div>
                  ))}
                  {/* Write-in candidates */}
                  {writeInCandidates.map((candidate, index) => (
                    <div key={index} className={`p-2 rounded flex items-center justify-between ${accentClass}`}>
                      <span className="font-medium text-sm">{candidate.name} (Write-in)</span>
                      <span className="font-bold text-sm">{candidate.votes}</span>
                    </div>
                  ))}
                  {/* Invalid ballots */}
                  {invalidBallots > 0 && (
                    <div className={`p-2 rounded flex items-center justify-between ${accentClass}`}>
                      <span className="font-medium text-sm text-red-600">Invalid Ballots</span>
                      <span className="font-bold text-sm text-red-600">{invalidBallots}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Vote Count Display */}
            <div className={`p-3 rounded ${accentClass}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Total Votes Cast</span>
                <Vote size={16} />
              </div>
              <div className="text-2xl font-bold">
                {electionScreen === 5 ? getTotalVotes() : '-'}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ElectionObserverView;